import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Buildings, Factory, UserCircle, Warning, Bell, ClockCounterClockwise,
  Wrench, ArrowsClockwise, ChartBar, TrendUp, TrendDown, ShieldCheck,
  MagnifyingGlass, Stack, Lightning, PenNib, Flask, TrashSimple,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_STYLE = {
  excellent: { ring: "ring-emerald-700/40", bg: "bg-emerald-500/5",  text: "text-emerald-300", label: "EXCELLENT" },
  good:      { ring: "ring-teal-700/40",    bg: "bg-teal-500/5",     text: "text-teal-300",    label: "GOOD" },
  watch:     { ring: "ring-amber-700/40",   bg: "bg-amber-500/5",    text: "text-amber-300",   label: "WATCH" },
  at_risk:   { ring: "ring-red-700/50",     bg: "bg-red-500/5",      text: "text-red-300",     label: "AT RISK" },
};

const PARTY_ICON = {
  organization: Buildings,
  facility:     Factory,
  contact:      UserCircle,
};

const TYPE_TABS = [
  { value: "all",          label: "All" },
  { value: "organization", label: "Organizations" },
  { value: "facility",     label: "Facilities" },
  { value: "contact",      label: "Contacts" },
];

const SORT_OPTIONS = [
  { value: "reliability", label: "Reliability (worst first)" },
  { value: "shipments",   label: "Shipments handled" },
  { value: "excursions",  label: "Excursions linked" },
  { value: "pending",     label: "Pending corrective actions" },
];

function formatLatency(min) {
  if (min == null) return "—";
  if (min < 60) return `${Math.round(min)}m`;
  const h = min / 60;
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

function TotalsBar({ totals, generatedAt, onRefresh, loading }) {
  const items = [
    { label: "Parties tracked",       value: totals.parties_tracked,            icon: Stack,            tone: "text-zinc-300" },
    { label: "Shipments handled",     value: totals.shipments_handled,          icon: ChartBar,         tone: "text-teal-300" },
    { label: "Excursions linked",     value: totals.excursions_linked,          icon: Warning,          tone: "text-amber-300" },
    { label: "Unresolved alerts",     value: totals.unresolved_alerts,          icon: Bell,             tone: "text-red-300" },
    { label: "Delayed signatures",    value: totals.delayed_signatures,         icon: PenNib,           tone: "text-amber-300" },
    { label: "Pending CAs",           value: totals.corrective_actions_pending, icon: Wrench,           tone: "text-amber-300" },
    { label: "Overdue CAs",           value: totals.overdue_corrective_actions, icon: ClockCounterClockwise, tone: "text-red-300" },
    { label: "At-risk parties",       value: totals.at_risk_parties,            icon: ShieldCheck,      tone: "text-red-300" },
  ];
  return (
    <div className="flex items-center justify-between gap-4 mb-4">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 flex-1" data-testid="accountability-totals">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <div key={it.label} className="px-3 py-2 rounded border border-zinc-800 bg-zinc-950/40">
              <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-wider text-zinc-500">
                <Icon size={11} weight="duotone" />
                {it.label}
              </div>
              <div className={`text-lg font-mono font-bold mt-0.5 ${it.tone}`}>{it.value ?? 0}</div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        {generatedAt && (
          <span className="text-[10px] font-mono text-zinc-500">
            {new Date(generatedAt).toUTCString().slice(17, 25)} UTC
          </span>
        )}
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-xs h-8"
          data-testid="accountability-refresh">
          <ArrowsClockwise size={12} weight="duotone" className={loading ? "animate-spin mr-1" : "mr-1"} />
          Refresh
        </Button>
      </div>
    </div>
  );
}

function ScoreRing({ score }) {
  // simple radial display, color by score band
  const color = score >= 80 ? "#10b981" : score >= 60 ? "#14b8a6" : score >= 40 ? "#f59e0b" : "#ef4444";
  const dash = (score / 100) * 138; // 2*pi*22 ≈ 138
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" className="shrink-0">
      <circle cx="28" cy="28" r="22" fill="none" stroke="#27272a" strokeWidth="4" />
      <circle cx="28" cy="28" r="22" fill="none" stroke={color} strokeWidth="4"
        strokeLinecap="round" strokeDasharray={`${dash} 138`} transform="rotate(-90 28 28)" />
      <text x="28" y="32" textAnchor="middle" fontSize="14" fontFamily="ui-monospace, monospace"
        fontWeight="700" fill={color}>{score}</text>
    </svg>
  );
}

function PartyCard({ card, onOpen }) {
  const Icon = PARTY_ICON[card.party_type] || UserCircle;
  const tier = TIER_STYLE[card.tier] || TIER_STYLE.good;
  return (
    <Card
      className={`p-4 bg-zinc-900/40 border-zinc-800 ring-1 ${tier.ring} hover:ring-2 transition-all cursor-pointer`}
      onClick={() => onOpen(card)}
      data-testid={`party-card-${card.party_id}`}>
      <div className="flex items-start gap-3">
        <ScoreRing score={card.reliability_score} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <Icon size={12} weight="duotone" className="text-zinc-400" />
            <span className="text-[9px] uppercase tracking-wider text-zinc-500">{card.party_type}</span>
            <span className={`ml-auto text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${tier.bg} ${tier.text}`}>
              {tier.label}
            </span>
          </div>
          <div className="text-sm font-semibold text-zinc-100 truncate" title={card.name}>{card.name}</div>
          {card.sub_label && (
            <div className="text-[10px] text-zinc-500 truncate" title={card.sub_label}>{card.sub_label}</div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-4">
        <Stat icon={ChartBar}        label="Shipments"    value={card.shipments_handled}            tone="text-teal-300" />
        <Stat icon={Warning}         label="Excursions"   value={card.excursions_linked}            tone={card.excursions_linked > 0 ? "text-amber-300" : "text-zinc-400"} />
        <Stat icon={Bell}            label="Unresolved"   value={card.unresolved_alerts}            tone={card.unresolved_alerts > 0 ? "text-red-300" : "text-zinc-400"} />
        <Stat icon={PenNib}          label="Delayed sig." value={card.delayed_signatures}           tone={card.delayed_signatures > 0 ? "text-amber-300" : "text-zinc-400"} />
        <Stat icon={Wrench}          label="Pending CAs"  value={card.corrective_actions_pending}   tone={card.corrective_actions_pending > 0 ? "text-amber-300" : "text-zinc-400"} />
        <Stat icon={ClockCounterClockwise} label="Overdue CAs" value={card.overdue_corrective_actions} tone={card.overdue_corrective_actions > 0 ? "text-red-300" : "text-zinc-400"} />
      </div>

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-800/80">
        <div>
          <div className="text-[9px] uppercase tracking-wider text-zinc-500">Avg ack</div>
          <div className="text-sm font-mono text-zinc-200">{formatLatency(card.avg_ack_minutes)}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wider text-zinc-500">Avg response</div>
          <div className="text-sm font-mono text-zinc-200">{formatLatency(card.avg_response_minutes)}</div>
        </div>
        <div className="text-right">
          <div className="text-[9px] uppercase tracking-wider text-zinc-500">Score</div>
          <div className="text-sm font-mono text-zinc-200 flex items-center gap-1 justify-end">
            {card.reliability_score >= 60 ? (
              <TrendUp size={11} className="text-emerald-400" weight="duotone" />
            ) : (
              <TrendDown size={11} className="text-red-400" weight="duotone" />
            )}
            {card.reliability_score}/100
          </div>
        </div>
      </div>
    </Card>
  );
}

function Stat({ icon: Icon, label, value, tone }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-zinc-500">
        <Icon size={10} weight="duotone" />
        {label}
      </div>
      <div className={`text-lg font-mono font-bold ${tone}`}>{value ?? 0}</div>
    </div>
  );
}

function PartyDetailDrawer({ open, onClose, card }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !card) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/accountability/party/${card.party_type}/${card.party_id}`);
        if (!cancelled) setDetail(data);
      } catch (e) {
        toast.error("Failed to load party detail");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, card]);

  if (!open || !card) return null;
  const Icon = PARTY_ICON[card.party_type] || UserCircle;
  const tier = TIER_STYLE[card.tier] || TIER_STYLE.good;

  return (
    <div className="fixed inset-0 bg-black/60 z-[1900] flex justify-end" onClick={onClose}>
      <div className="w-full md:w-[640px] bg-zinc-950 border-l border-zinc-800 h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()} data-testid="party-detail-drawer">
        <div className="p-5 border-b border-zinc-800 sticky top-0 bg-zinc-950 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ScoreRing score={card.reliability_score} />
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon size={12} weight="duotone" className="text-zinc-400" />
                  <span className="text-[9px] uppercase tracking-wider text-zinc-500">{card.party_type}</span>
                  <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${tier.bg} ${tier.text}`}>
                    {tier.label}
                  </span>
                </div>
                <h2 className="text-base font-semibold text-zinc-100">{card.name}</h2>
                {card.sub_label && <div className="text-[11px] text-zinc-500">{card.sub_label}</div>}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-zinc-500">Close</Button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {loading && <p className="text-xs text-zinc-500 italic">Loading…</p>}

          <section>
            <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Recent shipments</h3>
            {detail?.recent_shipments?.length > 0 ? (
              <div className="space-y-1.5">
                {detail.recent_shipments.slice(0, 12).map((s) => (
                  <div key={s.id} className="flex items-center justify-between p-2 rounded border border-zinc-800 bg-zinc-900/30">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-100 truncate">{s.name || s.id.slice(0, 8)}</div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.status}</div>
                    </div>
                    {s.compliance_score != null && (
                      <Badge className="bg-teal-500/10 text-teal-300 border-teal-700/40 text-[10px] h-5 font-mono">
                        {Math.round(s.compliance_score)}%
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[11px] italic text-zinc-500">No shipments link to this party yet.</p>
            )}
          </section>

          <section>
            <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Corrective actions assigned</h3>
            {detail?.corrective_actions?.length > 0 ? (
              <div className="space-y-1.5">
                {detail.corrective_actions.slice(0, 12).map((ca) => (
                  <div key={ca.id} className="p-2 rounded border border-zinc-800 bg-zinc-900/30">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-zinc-100">{ca.action_type}</span>
                      <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${
                        ca.status === "completed" ? "bg-emerald-500/15 text-emerald-300" :
                        ca.status === "acknowledged" ? "bg-sky-500/15 text-sky-300" :
                        ca.status === "escalated" ? "bg-red-500/15 text-red-300" :
                        "bg-amber-500/15 text-amber-300"
                      }`}>{ca.status}</span>
                    </div>
                    {ca.note && <p className="text-[11px] text-zinc-400 italic mt-0.5">{ca.note}</p>}
                    <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                      created {ca.created_at ? new Date(ca.created_at).toUTCString().slice(5, 22) : "—"}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[11px] italic text-zinc-500">No corrective actions assigned.</p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Ranked Scorecards (Phase 23) — rate-based ranking for Facilities + Carriers
// ----------------------------------------------------------------------------

function RatePill({ value, kind = "warn", suffix = "%" }) {
  if (value == null) {
    return <span className="text-[10px] font-mono text-zinc-600">—</span>;
  }
  const color =
    kind === "warn"
      ? value >= 50 ? "text-red-300" : value >= 20 ? "text-amber-300" : "text-emerald-300"
      : "text-zinc-200";
  return <span className={`text-sm font-mono ${color}`}>{value.toFixed(1)}{suffix}</span>;
}

function RankedScorecardsView({ type, refreshKey = 0 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generatedAt, setGeneratedAt] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/accountability/scorecards`, { params: { type } });
      setItems(data.items || []);
      setGeneratedAt(data.generated_at);
    } catch {
      toast.error("Failed to load ranked scorecards");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [type, refreshKey]);

  const isCarrier = type === "carrier";
  const Icon = isCarrier ? Buildings : Factory;
  const title = isCarrier ? "Carrier Scorecards" : "Facility Scorecards";

  return (
    <div data-testid={`ranked-${type}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon size={14} weight="duotone" className="text-teal-400" />
          <h3 className="text-sm font-semibold text-zinc-100">{title}</h3>
          <span className="text-[10px] font-mono text-zinc-500">
            ranked worst-first · {items.length} parties
          </span>
        </div>
        <div className="flex items-center gap-2">
          {generatedAt && (
            <span className="text-[10px] font-mono text-zinc-500">
              {new Date(generatedAt).toUTCString().slice(17, 25)} UTC
            </span>
          )}
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-xs h-7"
            data-testid={`ranked-refresh-${type}`}>
            <ArrowsClockwise size={11} className={loading ? "animate-spin mr-1" : "mr-1"} />
            Refresh
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-12 text-zinc-500 text-sm italic border border-zinc-800 rounded bg-zinc-900/30">
          {loading ? "Loading…" : `No ${isCarrier ? "carriers" : "facilities"} registered for this tenant yet.`}
        </div>
      ) : (
        <div className="overflow-x-auto border border-zinc-800 rounded bg-zinc-900/30">
          <table className="w-full text-xs" data-testid={`ranked-table-${type}`}>
            <thead className="bg-zinc-950/60 border-b border-zinc-800 text-[9px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2 w-8">#</th>
                <th className="text-left px-3 py-2 w-12">Score</th>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-right px-2 py-2">Ships</th>
                <th className="text-right px-2 py-2" title="% of shipments with ≥1 excursion">Excursion %</th>
                <th className="text-right px-2 py-2" title="% of shipments with unresolved critical alerts">Unresolved %</th>
                {isCarrier ? (
                  <>
                    <th className="text-right px-2 py-2" title="% of delivered shipments where end_time > eta">Late Delivery %</th>
                    <th className="text-right px-2 py-2">Avg Transit</th>
                  </>
                ) : (
                  <th className="text-right px-2 py-2" title="% of delivered shipments missing signed_at">Delayed Sig %</th>
                )}
                <th className="text-right px-2 py-2">Avg Ack</th>
                <th className="text-right px-2 py-2">Overdue</th>
                <th className="text-right px-3 py-2">Tier</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/60">
              {items.map((c, idx) => {
                const tier = TIER_STYLE[c.tier] || TIER_STYLE.good;
                return (
                  <tr key={c.party_id} className="hover:bg-zinc-800/30"
                    data-testid={`ranked-row-${c.party_id}`}>
                    <td className="px-3 py-2 font-mono text-zinc-500">{idx + 1}</td>
                    <td className="px-3 py-2 font-mono font-bold text-zinc-100">{c.reliability_score}</td>
                    <td className="px-3 py-2">
                      <div className="text-zinc-100">{c.name}</div>
                      {c.sub_label && <div className="text-[10px] text-zinc-500">{c.sub_label}</div>}
                    </td>
                    <td className="px-2 py-2 text-right font-mono text-zinc-200">{c.shipments_handled}</td>
                    <td className="px-2 py-2 text-right"><RatePill value={c.excursion_rate} /></td>
                    <td className="px-2 py-2 text-right"><RatePill value={c.unresolved_alert_rate} /></td>
                    {isCarrier ? (
                      <>
                        <td className="px-2 py-2 text-right"><RatePill value={c.late_delivery_rate} /></td>
                        <td className="px-2 py-2 text-right font-mono text-zinc-200">
                          {c.avg_transit_hours != null ? `${c.avg_transit_hours.toFixed(1)}h` : "—"}
                        </td>
                      </>
                    ) : (
                      <td className="px-2 py-2 text-right"><RatePill value={c.delayed_signature_rate} /></td>
                    )}
                    <td className="px-2 py-2 text-right font-mono text-zinc-200">{formatLatency(c.avg_ack_minutes)}</td>
                    <td className={`px-2 py-2 text-right font-mono ${c.overdue_corrective_actions > 0 ? "text-red-300" : "text-zinc-500"}`}>
                      {c.overdue_corrective_actions}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${tier.bg} ${tier.text}`}>
                        {tier.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AccountabilityDashboard({ isAdmin = false }) {
  const [type, setType] = useState("all");
  const [sort, setSort] = useState("reliability");
  const [data, setData] = useState({ items: [], totals: {}, generated_at: null });
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [drawerCard, setDrawerCard] = useState(null);
  const [demoStatus, setDemoStatus] = useState({ seeded: false, counts: {} });
  const [demoBusy, setDemoBusy] = useState(false);
  const [viewMode, setViewMode] = useState("cards"); // 'cards' | 'ranked'
  const [refreshNonce, setRefreshNonce] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/accountability/summary`, {
        params: { type, sort, limit: 500 },
      });
      setData(data);
    } catch (e) {
      toast.error("Failed to load accountability summary");
    } finally {
      setLoading(false);
    }
  };

  const loadDemoStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/accountability/demo-seed/status`);
      setDemoStatus(data);
    } catch {/* noop */}
  };

  const seedDemo = async () => {
    setDemoBusy(true);
    const t = toast.loading("Seeding demo dataset…");
    try {
      const { data: res } = await axios.post(`${API}/accountability/demo-seed`);
      toast.success(
        `Seeded ${res.created.shipments} shipments · ${res.created.alerts} alerts · ${res.created.corrective_actions} CAs`,
        { id: t },
      );
      setRefreshNonce((n) => n + 1);
      await Promise.all([load(), loadDemoStatus()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Demo seed failed", { id: t });
    } finally {
      setDemoBusy(false);
    }
  };

  const clearDemo = async () => {
    if (!window.confirm("Remove every demo record (parties, shipments, alerts, corrective actions) from this tenant?")) return;
    setDemoBusy(true);
    const t = toast.loading("Clearing demo dataset…");
    try {
      const { data: res } = await axios.delete(`${API}/accountability/demo-seed`);
      toast.success(`Removed ${res.total} demo records`, { id: t });
      setRefreshNonce((n) => n + 1);
      await Promise.all([load(), loadDemoStatus()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Demo clear failed", { id: t });
    } finally {
      setDemoBusy(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [type, sort]);
  useEffect(() => { if (isAdmin) loadDemoStatus(); }, [isAdmin]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data.items;
    return data.items.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.sub_label || "").toLowerCase().includes(q)
    );
  }, [data.items, query]);

  return (
    <div className="max-w-[1600px] mx-auto" data-testid="accountability-dashboard">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100 flex items-center gap-2">
            <Lightning size={18} weight="duotone" className="text-teal-400" />
            Accountability Dashboard
          </h1>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            Per-party operational scorecards · chain-of-custody · alert ack & corrective-action timing
          </p>
        </div>

        {isAdmin && (
          <div className="flex items-center gap-2">
            {demoStatus.seeded ? (
              <>
                <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded bg-amber-500/15 text-amber-300 border border-amber-700/40"
                  data-testid="demo-active-pill">
                  DEMO DATA ACTIVE
                </span>
                <Button variant="outline" size="sm" onClick={clearDemo} disabled={demoBusy}
                  className="border-red-700/60 text-red-300 hover:bg-red-500/10 text-xs h-8"
                  data-testid="demo-clear-btn">
                  <TrashSimple size={11} className="mr-1" /> Clear Demo Data
                </Button>
              </>
            ) : (
              <Button variant="outline" size="sm" onClick={seedDemo} disabled={demoBusy}
                className="border-amber-700/60 text-amber-300 hover:bg-amber-500/10 text-xs h-8"
                data-testid="demo-seed-btn">
                <Flask size={11} className="mr-1" /> Seed Demo Tiers
              </Button>
            )}
          </div>
        )}
      </div>

      {isAdmin && demoStatus.seeded && (
        <div className="mb-3 px-3 py-2 rounded border border-amber-700/40 bg-amber-500/5 flex items-center gap-2"
          data-testid="demo-banner">
          <Flask size={12} weight="duotone" className="text-amber-300" />
          <span className="text-[11px] text-amber-300">
            Demo dataset active for this tenant — every card and metric tagged
            <span className="font-mono"> [DEMO] </span>
            is auto-seeded and can be removed via <span className="font-semibold">Clear Demo Data</span>.
            Counts: {demoStatus.counts.shipments || 0} shipments · {demoStatus.counts.alerts || 0} alerts · {demoStatus.counts.corrective_actions || 0} corrective actions.
          </span>
        </div>
      )}

      <TotalsBar totals={data.totals || {}} generatedAt={data.generated_at}
        onRefresh={load} loading={loading} />

      {/* View mode toggle */}
      <div className="flex items-center gap-1 p-0.5 bg-zinc-900 rounded border border-zinc-800 mb-4 w-fit"
        data-testid="view-mode-toggle">
        {[
          { v: "cards",  label: "Card Grid" },
          { v: "ranked", label: "Ranked Scorecards" },
        ].map((m) => (
          <button key={m.v} type="button" onClick={() => setViewMode(m.v)}
            className={`text-xs h-7 px-3 rounded ${viewMode === m.v ? "bg-teal-500/15 text-teal-300 font-semibold" : "text-zinc-500 hover:text-zinc-300"}`}
            data-testid={`viewmode-${m.v}`}>
            {m.label}
          </button>
        ))}
      </div>

      {viewMode === "ranked" ? (
        <div className="space-y-5" data-testid="ranked-view">
          <RankedScorecardsView type="facility" refreshKey={refreshNonce} />
          <RankedScorecardsView type="carrier"  refreshKey={refreshNonce} />
        </div>
      ) : (
        <>
      {/* Filter bar */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-1 p-0.5 bg-zinc-900 rounded border border-zinc-800">
          {TYPE_TABS.map((t) => (
            <button key={t.value} onClick={() => setType(t.value)} type="button"
              className={`text-xs h-7 px-3 rounded ${type === t.value ? "bg-teal-500/15 text-teal-300 font-semibold" : "text-zinc-500 hover:text-zinc-300"}`}
              data-testid={`type-tab-${t.value}`}>
              {t.label}
            </button>
          ))}
        </div>

        <select value={sort} onChange={(e) => setSort(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded text-xs text-zinc-200 px-2.5 h-8 focus:outline-none"
          data-testid="accountability-sort">
          {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>

        <div className="relative">
          <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name…"
            className="bg-zinc-900 border-zinc-800 text-xs h-8 pl-7 w-56"
            data-testid="accountability-search" />
        </div>

        <span className="text-[10px] font-mono text-zinc-500 ml-auto">
          {filtered.length} of {data.items.length} parties
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-zinc-500 text-sm italic">
          {loading ? "Loading…" : "No parties match the current filters."}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="accountability-grid">
          {filtered.map((c) => (
            <PartyCard key={`${c.party_type}-${c.party_id}`} card={c} onOpen={setDrawerCard} />
          ))}
        </div>
      )}
        </>
      )}

      <PartyDetailDrawer
        open={!!drawerCard}
        card={drawerCard}
        onClose={() => setDrawerCard(null)}
      />
    </div>
  );
}
